// `crear_formato_personalizado`: el agente ve, crea y usa los formatos personalizados de la
// marca — ADR-2026-10-21-el-agente-ve-crea-y-usa-los-formatos-personalizados.
//
// VER: la lista va en el bloque volátil del prompt (`describirFormatosPersonalizados`), con
// el slug de cada uno. USAR: el slug va tal cual en `format` de generate_image. CREAR: esta
// tool, que escribe en la misma lista que el selector de Formato y que "Formato de descarga"
// de Funciones avanzadas (`agregarFormatoPersonalizado`). Es instantáneo y gratis: no hay
// card, como `configurar_preferencias`.
//
// Puro: lo que lee y lo que guarda entra por `DepsDeFormatos`, así se prueba en memoria.

import type { HerramientaChat } from './tipos';
import {
  LIMITES_DE_FORMATO,
  etiquetaDeFormatoPersonalizado,
  slugDeFormatoPersonalizado,
  slugEstandarMasCercano,
  type FormatoPersonalizado,
} from '../utils/formatoPersonalizado';
import type { FormatoDeSalida } from '../utils/formatoDeSalida';

export const NOMBRE_CREAR_FORMATO_PERSONALIZADO = 'crear_formato_personalizado';

const ARCHIVOS: readonly FormatoDeSalida[] = ['jpg', 'png', 'webp'];

export interface DepsDeFormatos {
  /** Los formatos de la marca activa. */
  formatos: () => Promise<readonly FormatoPersonalizado[]>;
  /** Suma uno a la marca. Puede devolver OTRO (`existente`) si el mismo ya estaba. */
  crear: (
    f: FormatoPersonalizado,
  ) => Promise<{ ok: boolean; error?: string; formato?: FormatoPersonalizado; existente?: boolean }>;
  /** Para un id reproducible en los tests. */
  nuevoId?: () => string;
}

export const HERRAMIENTA_CREAR_FORMATO_PERSONALIZADO: HerramientaChat = {
  name: NOMBRE_CREAR_FORMATO_PERSONALIZADO,
  description:
    'Crea un FORMATO PERSONALIZADO de imagen para la marca: una medida exacta en píxeles (ancho × alto) con el tipo de archivo y, si quiere, un peso máximo. Es lo que el usuario necesita cuando una tienda o una plataforma le pide una medida puntual ("1000×1500 para Tiendanube", "1080×1350 en WebP de menos de 200 KB"). Instantáneo y GRATIS: no genera nada ni hay card. Queda guardado en la marca (lo ve en el selector de Formato y en Configuración → Funciones avanzadas → Formato de descarga).\n' +
    'Los que YA existen los tenés a la vista en cada turno (bloque "FORMATOS PERSONALIZADOS DE LA MARCA"): si el que pide ya está, NO lo crees de nuevo — usá su slug. Para generar con uno, pasá su slug (`perfil:…`) TAL CUAL en `format` de generate_image: la imagen se genera en la proporción estándar más parecida y queda recortada al centro a la medida exacta; el archivo y el peso se aplican al descargar. Si el usuario pide una proporción común (4:5, 9:16, 1:1…) y no una medida en píxeles, NO uses esto: pasá el slug estándar.\n' +
    `Devuelve el formato creado con su slug y la lista completa. Contale al usuario la medida y el archivo con los que quedó, y si algo salió rechazado, el valor válido (lados de ${LIMITES_DE_FORMATO.ladoMin} a ${LIMITES_DE_FORMATO.ladoMax} px; peso de ${LIMITES_DE_FORMATO.pesoMin} a ${LIMITES_DE_FORMATO.pesoMax} KB).`,
  input_schema: {
    type: 'object',
    properties: {
      ancho: { type: 'number', description: `Ancho en píxeles (${LIMITES_DE_FORMATO.ladoMin} a ${LIMITES_DE_FORMATO.ladoMax}).` },
      alto: { type: 'number', description: `Alto en píxeles (${LIMITES_DE_FORMATO.ladoMin} a ${LIMITES_DE_FORMATO.ladoMax}).` },
      nombre: {
        type: 'string',
        description: 'Opcional. Cómo lo va a ver el usuario, en pocas palabras: la tienda o el uso ("Tiendanube", "Banner web"). Sin nombre se muestra la medida.',
      },
      archivo: {
        type: 'string',
        description: 'Opcional. Tipo de archivo al descargar: jpg (default, el más compatible), png (sin pérdida, pesa más) o webp (el más liviano).',
      },
      peso_max_kb: {
        type: 'number',
        description: `Opcional. Peso máximo del archivo descargado, en KB (${LIMITES_DE_FORMATO.pesoMin} a ${LIMITES_DE_FORMATO.pesoMax}). Sólo si el usuario lo pidió; sin tope se guarda en la mejor calidad. Con png no aplica.`,
      },
    },
    required: ['ancho', 'alto'],
  },
};

/** Lo que vuelve como `tool_result`, legible: el agente lo repite. `type` y no `interface`
 *  porque el ejecutor del chat devuelve `Record<string, unknown>` y una interface no entra. */
export type ResultadoCrearFormato = {
  ok: boolean;
  formato?: { slug: string; etiqueta: string; ancho: number; alto: number; archivo: FormatoDeSalida; peso_max_kb: number | null; se_genera_en: string };
  rechazo?: string;
  nota?: string;
  formatos_ahora: string[];
};

const resumen = (f: FormatoPersonalizado): string =>
  `${slugDeFormatoPersonalizado(f)} — ${etiquetaDeFormatoPersonalizado(f)}, ${f.formato.toUpperCase()}${
    f.pesoMaxKB ? `, hasta ${f.pesoMaxKB} KB` : ''
  }`;

const entero = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? Math.round(n) : null;
};

const idNuevo = (): string => `p_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export async function resolverCrearFormatoPersonalizado(
  entrada: unknown,
  deps: DepsDeFormatos,
): Promise<ResultadoCrearFormato> {
  const p = (entrada && typeof entrada === 'object' ? entrada : {}) as Record<string, unknown>;
  const L = LIMITES_DE_FORMATO;
  const actuales = await deps.formatos();
  const lista = () => actuales.map(resumen);

  const ancho = entero(p.ancho);
  const alto = entero(p.alto);
  const ladoOk = (n: number | null): n is number => n !== null && n >= L.ladoMin && n <= L.ladoMax;
  if (!ladoOk(ancho) || !ladoOk(alto)) {
    return { ok: false, rechazo: `El ancho y el alto van de ${L.ladoMin} a ${L.ladoMax} px.`, formatos_ahora: lista() };
  }
  const archivoCrudo = typeof p.archivo === 'string' ? p.archivo.trim().toLowerCase().replace('jpeg', 'jpg') : 'jpg';
  if (!(ARCHIVOS as readonly string[]).includes(archivoCrudo)) {
    return { ok: false, rechazo: 'El archivo tiene que ser jpg, png o webp.', formatos_ahora: lista() };
  }
  const archivo = archivoCrudo as FormatoDeSalida;
  let pesoMaxKB: number | null = null;
  if (p.peso_max_kb !== undefined && p.peso_max_kb !== null && p.peso_max_kb !== '') {
    const peso = entero(p.peso_max_kb);
    if (peso === null || peso < L.pesoMin || peso > L.pesoMax) {
      return { ok: false, rechazo: `El peso máximo va de ${L.pesoMin} a ${L.pesoMax} KB (o no mandes el campo).`, formatos_ahora: lista() };
    }
    pesoMaxKB = archivo === 'png' ? null : peso;
  }
  const nombre = typeof p.nombre === 'string' ? p.nombre.trim().slice(0, 60) : '';

  // Igual medida y archivo: es el mismo formato y se devuelve ése sin duplicarlo — salvo
  // que el usuario haya pedido un peso máximo DISTINTO del que tiene: el peso es parte de lo
  // que sale al descargar, así que ahí se crea otro en vez de decir "listo" con un tope que
  // no se aplicó (revisión de Greptile al PR #197).
  const pidioPeso = p.peso_max_kb !== undefined && p.peso_max_kb !== null && p.peso_max_kb !== '';
  const existente = actuales.find(
    (f) => f.ancho === ancho && f.alto === alto && f.formato === archivo && (!pidioPeso || (f.pesoMaxKB ?? null) === pesoMaxKB),
  );
  const armar = (f: FormatoPersonalizado) => ({
    slug: slugDeFormatoPersonalizado(f),
    etiqueta: etiquetaDeFormatoPersonalizado(f),
    ancho: f.ancho,
    alto: f.alto,
    archivo: f.formato,
    peso_max_kb: f.pesoMaxKB,
    se_genera_en: slugEstandarMasCercano(f.ancho, f.alto),
  });
  if (existente) {
    return {
      ok: true,
      formato: armar(existente),
      nota: 'Ya existía un formato con esa medida y ese archivo: no se creó otro. Usá su slug.',
      formatos_ahora: lista(),
    };
  }

  const nuevo: FormatoPersonalizado = { id: (deps.nuevoId ?? idNuevo)(), nombre, ancho, alto, formato: archivo, pesoMaxKB };
  const r = await deps.crear(nuevo);
  if (!r.ok) {
    return { ok: false, rechazo: r.error ?? 'No se pudo guardar el formato. Probá de nuevo.', formatos_ahora: lista() };
  }
  // Lo que quedó guardado manda: si otra llamada de la misma respuesta ya había creado el
  // mismo formato, el store devuelve ése (`existente`), no el que armamos acá.
  const guardado = r.formato ?? nuevo;
  // La lista se relee después de guardar (el store la publica entera); si el origen no la
  // refrescó, el guardado se suma a mano para no listarlo dos veces ni ninguna.
  const despues = await deps.formatos();
  const completa = despues.some((f) => f.id === guardado.id) ? despues : [...despues, guardado];
  return {
    ok: true,
    formato: armar(guardado),
    ...(r.existente ? { nota: 'Ya existía un formato igual: no se creó otro. Usá su slug.' } : {}),
    formatos_ahora: completa.map(resumen),
  };
}

/** El bloque del prompt: los formatos de la marca con su slug, o que no hay ninguno. */
export function describirFormatosPersonalizados(formatos: readonly FormatoPersonalizado[]): string {
  const cabecera =
    'FORMATOS PERSONALIZADOS DE LA MARCA (medida exacta en píxeles + tipo de archivo; los crea el usuario en el selector de Formato o vos con `crear_formato_personalizado`):';
  const uso =
    'Para generar con uno, pasá su slug `perfil:…` TAL CUAL en `format` de generate_image: la imagen sale recortada a esa medida exacta. Si el usuario pide una medida en píxeles que no está acá, crealo con la tool y generá con su slug; no inventes el slug.';
  if (formatos.length === 0) {
    return `${cabecera}\n(ninguno todavía)\n${uso}`;
  }
  return `${cabecera}\n${formatos.map((f) => `- ${resumen(f)}`).join('\n')}\n${uso}`;
}
